import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ClipboardTooltip } from "@/components/utils/clipboard-tooltip";
import { cn } from "@/lib/utils";

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  confirmClassName?: string;
  isCritical?: boolean;
  itemName?: string;
  confirmationValue?: string;
  onConfirmationChange?: (value: string) => void;
  canConfirm?: boolean;
  children?: React.ReactNode;
  /**
   * Si true, el botón de confirmación recibe el foco al abrir el modal (Radix
   * `AlertDialog.Action` con `autoFocus`). Por defecto `false` para preservar
   * el comportamiento histórico (foco en Cancel). Útil cuando la acción
   * principal es la esperada (p.ej. procesar una factura).
   */
  autoFocusConfirm?: boolean;
}

export function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirmar acción",
  description,
  confirmText = "Confirmar",
  cancelText = "Cancelar",
  confirmClassName = "",
  isCritical = false,
  itemName,
  confirmationValue = "",
  onConfirmationChange,
  canConfirm = true,
  children,
  autoFocusConfirm = false,
}: ConfirmationModalProps) {
  const handleConfirm = () => {
    if (canConfirm) {
      onConfirm();
      onClose();
    }
  };

  const isCriticalConfirmed = isCritical
    ? confirmationValue?.trim() === itemName?.trim()
    : true;

  const canProceed = canConfirm && isCriticalConfirmed;

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {(description || (isCritical && itemName)) && (
            <AlertDialogDescription className="text-muted-foreground text-sm">
              {description}
              {isCritical && itemName && (
                <>
                  <br />
                  <strong className="text-destructive">
                    Esta es una acción crítica.
                  </strong>{" "}
                  Para confirmar, escribe el nombre del elemento:
                  <br />
                  <br />
                  <ClipboardTooltip value={itemName} content={itemName} />
                </>
              )}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>

        {children}

        {isCritical && itemName && (
          <div className="space-y-2">
            <Label htmlFor="confirmation">
              Escribe o copia y pega "{itemName}" para confirmar
            </Label>
            <Input
              autoFocus
              id="confirmation"
              value={confirmationValue}
              onChange={(e) => onConfirmationChange?.(e.target.value)}
              placeholder={itemName}
              autoComplete="off"
              required
            />
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel>{cancelText}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={!canProceed}
            autoFocus={autoFocusConfirm}
            className={cn(
              confirmClassName,
              !confirmClassName &&
                "bg-destructive text-destructive-foreground hover:bg-destructive/90",
            )}
          >
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
